import React, { useState, useContext, useEffect, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { MdOutlineEdit, MdOutlineDelete, MdOutlineManageAccounts } from "react-icons/md";
import { toast } from "react-toastify";
import AppContext from "../../context/AppContext";
import { getRoleList, getRoleById, createRole, updateRole, deleteRole } from "../../service/Role";
import { getAllByOrganization } from "../../service/User";
import { RxCross2 } from "react-icons/rx";
import { Spinner } from "../../Spinner";
import translations from "../../components/common/Translations";
import CommonButton from "../../components/common/CommonButton";
import CreateRoleModal from "./CreateRoleModal";
import UpdateRoleModal from "./UpdateRoleModal";
import PermissionModal from "./PermissionModal";
import { getPermissionByScreenCode, canShowActionColumn } from "../../utils/permissions";

const WhiteTooltip = ({ text, children, position = "top" }) => {
  const [show, setShow] = useState(false);
  const posClass = position === "top" ? "bottom-full left-1/2 -translate-x-1/2 mb-2" : "top-full left-1/2 -translate-x-1/2 mt-2";
  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && text && (
        <div
          className={`absolute z-[9999] px-2 py-1.5 text-sm text-gray-800 bg-white border border-gray-200 rounded-md shadow-lg whitespace-nowrap ${posClass}`}
          role="tooltip"
        >
          {text}
        </div>
      )}
    </div>
  );
};

const RoleManagement = () => {
  const { theme, langMode, userType, selectedOrgId } = useContext(AppContext);
  const [roleList, setRoleList] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [organizationList, setOrganizationList] = useState([]);
  const [loadingRoleDetail, setLoadingRoleDetail] = useState(false);
  const [loadingRoleId, setLoadingRoleId] = useState(null);
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [roleForPermission, setRoleForPermission] = useState(null);

  const isSuperAdmin = userType === "super_admin";
  const rolePerm = getPermissionByScreenCode("ROLE_MANAGEMENT");
  const showRoleActionColumn = isSuperAdmin || canShowActionColumn("ROLE_MANAGEMENT");
  const canCreateRole = isSuperAdmin || Boolean(rolePerm?.canCreate);
  const canUpdateRole = isSuperAdmin || Boolean(rolePerm?.canUpdate);
  const canDeleteRole = isSuperAdmin || Boolean(rolePerm?.canDelete);
  const totalPages = Math.max(0, Math.ceil(totalValue / pageSize) - 1);
  const startIndex = currentPage * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalValue);
  const totalItems = totalValue;

  const [createForm, setCreateForm] = useState({
    roleName: "",
    description: "",
    organizationId: selectedOrgId,
    screenPermissions: {}, // { screenId: { canView, canCreate, canUpdate, canDelete } }
  });
  const [updateForm, setUpdateForm] = useState({
    roleName: "",
    description: "",
    organizationId: null,
    screenPermissions: {}, // { screenId: { canView, canCreate, canUpdate, canDelete } }
  });

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const orgId = isSuperAdmin ? selectedOrgId : undefined;
      const res = await getRoleList(orgId);
      const list = Array.isArray(res) ? res : res?.roleList ?? res?.content ?? [];
      // const total = res?.totalSize ?? res?.totalElements ?? list.length;
      setRoleList(list);
      // setTotalValue(total);
    } catch (err) {
      console.error(err);
      setRoleList([]);
      // setTotalValue(0);
      toast.error(translations[langMode].anerroroccurred || "Failed to load roles");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const list = await getAllByOrganization();
      setOrganizationList(Array.isArray(list) ? list : list?.organizationList ?? []);
    } catch (err) {
      console.error(err);
      setOrganizationList([]);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [currentPage, pageSize, selectedOrgId]);

  const handleCreate = async () => {
    if (!createForm.roleName?.trim()) {
      toast.warning(translations[langMode].roleName + " is required");
      return;
    }
    if (isSuperAdmin && !selectedOrgId) {
      toast.warning(translations[langMode].selectOrg + " is required");
      return;
    }
    setLoading(true);
    try {
      const screenPermissionsArray = Object.entries(createForm.screenPermissions || {}).map(([screenId, perms]) => ({
        screenId: parseInt(screenId, 10),
        ...perms,
      }));
      const payload = {
        name: createForm.roleName.trim(),
        description: (createForm.description || "").trim(),
        organizationId: isSuperAdmin ? selectedOrgId : undefined,
        screenPermissions: screenPermissionsArray,
      };
      const data = await createRole(payload);
      if (data?.header?.code === 600 || data?.id) {
        toast.success("Role created successfully");
        setCreateOpen(false);
        setCreateForm({ roleName: "", description: "", organizationId: selectedOrgId, screenPermissions: {} });
        fetchRoles();
      } else {
        toast.error(data?.body?.value || "Failed to create role");
      }
    } catch (err) {
      toast.error(err?.response?.data?.body?.value || "Failed to create role");
    } finally {
      setLoading(false);
    }
  };

  const openUpdate = async (role) => {
    const roleId = role?.id ?? role?.roleId;
    if (!roleId) return;
    setLoadingRoleDetail(true);
    setLoadingRoleId(roleId);
    try {
      const roleDetail = await getRoleById(roleId);
      setEditingRole(roleDetail);
      // Convert screenPermissions array to object format: { screenId: { canView, canCreate, canUpdate, canDelete } }
      const screenPermissionsObj = {};
      const permsList = roleDetail?.screenPermissions;
      if (Array.isArray(permsList)) {
        permsList.forEach((perm) => {
          const sid = perm.screenId;
          if (sid != null) {
            screenPermissionsObj[sid] = {
              canView: perm.canView ?? false,
              canCreate: perm.canCreate ?? false,
              canUpdate: perm.canUpdate ?? false,
              canDelete: perm.canDelete ?? false,
            };
          }
        });
      }
      setUpdateForm({
        roleName: roleDetail?.name ?? roleDetail?.roleName ?? "",
        description: roleDetail?.description ?? "",
        organizationId: roleDetail?.organizationId ?? null,
        screenPermissions: screenPermissionsObj,
      });
      if (isSuperAdmin) fetchOrganizations();
      setUpdateOpen(true);
    } catch (err) {
      console.error(err);
      toast.error(translations[langMode].anerroroccurred || err?.response?.data?.body?.value || "Failed to load role details");
    } finally {
      setLoadingRoleDetail(false);
      setLoadingRoleId(null);
    }
  };

  const handleUpdate = async () => {
    if (!editingRole?.id && !editingRole?.roleId) return;
    if (!updateForm.roleName?.trim()) {
      toast.warning(translations[langMode].roleName + " is required");
      return;
    }
    const id = editingRole.id ?? editingRole.roleId;
    setLoading(true);
    try {
      const screenPermissionsArray = Object.entries(updateForm.screenPermissions || {}).map(([screenId, perms]) => ({
        screenId: parseInt(screenId, 10),
        ...perms,
      }));
      const payload = {
        name: updateForm.roleName.trim(),
        description: (updateForm.description || "").trim(),
        organizationId: isSuperAdmin ? updateForm.organizationId : undefined,
        screenPermissions: screenPermissionsArray,
      };
      const data = await updateRole(id, payload);
      if (data?.header?.code === 600 || data?.id) {
        toast.success("Role updated successfully");
        setUpdateOpen(false);
        setEditingRole(null);
        fetchRoles();
      } else {
        toast.error(data?.body?.value || "Failed to update role");
      }
    } catch (err) {
      toast.error(err?.response?.data?.body?.value || "Failed to update role");
    } finally {
      setLoading(false);
    }
  };

  const openDelete = (role) => {
    setRoleToDelete(role);
    setDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteOpen(false);
    setRoleToDelete(null);
  };

  const handleDelete = async () => {
    if (!roleToDelete) return;
    const id = roleToDelete.id ?? roleToDelete.roleId;
    setLoading(true);
    try {
      const data = await deleteRole(id);
      if (data?.header?.code === 600 || data === undefined) {
        toast.success("Role deleted successfully");
        closeDeleteModal();
        fetchRoles();
      } else {
        toast.error(data?.body?.value || "Failed to delete role");
      }
    } catch (err) {
      toast.error(err?.response?.data?.body?.value || "Failed to delete role");
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 0) setCurrentPage((p) => p - 1);
  };
  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((p) => p + 1);
  };

  const orgOptions = organizationList.map((org) => ({
    value: org.organizationId ?? org.id,
    label: org.organizationName ?? org.name ?? org.organizationId ?? org.id,
  }));

  const tableHeaders = [
    "S.L",
    translations[langMode].roleName,
    translations[langMode].description,
    ...(isSuperAdmin ? [translations[langMode].OrganizationName] : []),
    ...(showRoleActionColumn ? [translations[langMode].Action] : []),
  ];

  const inputClass = `w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : "border-gray-300"}`;

  return (
    <>
      <div className={`w-full h-full ${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""}`}>
        <div className="flex flex-wrap justify-between items-center">
          <h4
            className={`uppercase ml-5 inline-flex items-center gap-2 mt-5 font-semibold whitespace-nowrap text-gray-800 ${theme === "dark" ? "dark-mode " : theme === "high-contrast" ? "high-contrast " : ""}`}
          >
            {translations[langMode].roleManagement}
          </h4>
          {canCreateRole && (
            <CommonButton
              variant="primary"
              tooltipText={translations[langMode].createRole}
              onClick={() => {
                setCreateForm((f) => ({ ...f, organizationId: selectedOrgId }));
                setCreateOpen(true);
              }}
              className="mr-5 mt-5"
            >
              {translations[langMode].createRole}
            </CommonButton>
          )}
        </div>

        <div className={`${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""} w-full sm:w-auto mt-3 px-4 bg-white sm:rounded-lg`}>
      
          <div className="overflow-x-auto my-5">
            <div className="relative max-h-[450px] overflow-y-auto rounded-xl shadow-lg border border-gray-100">
              <table className="min-w-full text-sm text-left text-gray-700">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 z-10">
                  <tr>
                    {tableHeaders.map((header, index) => (
                      <th key={index} scope="col" className="px-6 py-3 text-xs font-semibold tracking-wider uppercase text-gray-600">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                {loading && <Spinner />}
                <tbody className="bg-white divide-y divide-gray-100">
                  {roleList && roleList.length > 0 ? (
                    roleList.map((role, index) => (
                      <tr key={role.id ?? role.roleId ?? index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">{startIndex + index + 1}</td>
                        <td className="px-6 py-4 whitespace-nowrap font-medium">{role.roleName ?? role.name ?? "-"}</td>
                        <td className="px-6 py-4">{role.description ?? "-"}</td>
                        {isSuperAdmin && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            {organizationList.find((o) => (o.organizationId ?? o.id) === (role.organizationId ?? role.organizationId))?.organizationName ??
                              role.organizationName ??
                              role.organizationId ??
                              "-"}
                          </td>
                        )}
                        {showRoleActionColumn && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              {canUpdateRole && (
                                <>
                                  <WhiteTooltip text={loadingRoleId === (role.id ?? role.roleId) ? "Loading..." : translations[langMode].updateRole}>
                                    <button
                                      type="button"
                                      onClick={() => openUpdate(role)}
                                      disabled={loadingRoleId !== null && (role.id ?? role.roleId) === loadingRoleId}
                                      className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                      aria-label={translations[langMode].updateRole}
                                    >
                                      <MdOutlineEdit size={20} />
                                    </button>
                                  </WhiteTooltip>
                                  <WhiteTooltip text="Permission">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setRoleForPermission(role);
                                        setPermissionModalOpen(true);
                                      }}
                                      className="p-2 rounded-lg text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/30 transition-colors"
                                      aria-label="Permission"
                                    >
                                      <MdOutlineManageAccounts size={20} />
                                    </button>
                                  </WhiteTooltip>
                                </>
                              )}
                              {canDeleteRole && (
                                <WhiteTooltip text={translations[langMode].deleteRole}>
                                  <button
                                    type="button"
                                    onClick={() => openDelete(role)}
                                    className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30 transition-colors"
                                    aria-label={translations[langMode].deleteRole}
                                  >
                                    <MdOutlineDelete size={20} />
                                  </button>
                                </WhiteTooltip>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={tableHeaders.length} className="text-center py-6 text-gray-500">
                        {translations[langMode].noRolesFound}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <CreateRoleModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setCreateForm({ roleName: "", description: "", organizationId: selectedOrgId, screenPermissions: {} });
        }}
        form={createForm}
        setForm={setCreateForm}
        onSave={handleCreate}
        loading={loading}
      />

      <UpdateRoleModal
        open={updateOpen}
        onClose={() => setUpdateOpen(false)}
        form={updateForm}
        setForm={setUpdateForm}
        orgOptions={orgOptions}
        isSuperAdmin={isSuperAdmin}
        onSave={handleUpdate}
        loading={loading}
      />

      <PermissionModal
        open={permissionModalOpen}
        onClose={() => {
          setPermissionModalOpen(false);
          setRoleForPermission(null);
        }}
        role={roleForPermission}
        onSaved={fetchRoles}
      />

      {/* Delete role – confirmation modal (must confirm before delete) */}
      <Transition.Root show={!!roleToDelete && deleteOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeDeleteModal}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" />
          </Transition.Child>
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className={`relative rounded-2xl shadow-xl sm:max-w-md w-full ${theme === "dark" ? "dark-mode bg-gray-800" : theme === "high-contrast" ? "high-contrast bg-white" : "bg-white"}`}>
                  <div className="px-5 py-6">
                    <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {translations[langMode].deleteRole}
                    </Dialog.Title>
                    <p className="text-gray-700 dark:text-gray-200">
                      {translations[langMode].confirmDeleteRole}
                      {roleToDelete?.roleName != null && roleToDelete.roleName !== "" && (
                        <span className="font-medium block mt-2">"{roleToDelete.roleName}"</span>
                      )}
                    </p>
                  </div>
                  <div className="flex justify-end items-center gap-3 px-5 py-4 border-t border-gray-200 dark:border-gray-600">
                    <CommonButton variant="danger" onClick={closeDeleteModal}>{translations[langMode].cancle || "Cancel"}</CommonButton>
                    <CommonButton variant="danger" onClick={handleDelete}>{translations[langMode].deleteRole}</CommonButton>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default RoleManagement;
